import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { getRemoteConfig, putRemoteConfig } from "../api/mobileapp";

const PLATFORMS = ["shared", "ios", "android"];

export default function Defaults() {
  const { profile } = useOutletContext();
  const isAdmin = profile?.role === "admin";

  const [platform, setPlatform] = useState("shared");
  const [text, setText] = useState("{}");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (p) => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const cfg = await getRemoteConfig(p);
      setText(JSON.stringify(cfg.config ?? {}, null, 2));
      setUpdatedAt(cfg.updated_at);
    } catch {
      // 404 = no config stored yet for this platform
      setText("{}");
      setUpdatedAt(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(platform);
  }, [platform, load]);

  const save = async () => {
    setError("");
    setSuccess("");

    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      setError("Invalid JSON — fix the syntax before saving.");
      return;
    }

    setSaving(true);
    try {
      const r = await putRemoteConfig(platform, parsed);
      setSuccess(`Defaults saved for "${platform}".`);
      setUpdatedAt(r.updated_at);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2 className="page-title">Defaults</h2>
      <p className="page-sub">
        Remote configuration defaults delivered to the mobile app (feature flags &amp; settings)
      </p>

      <div className="card">
        <div className="row" style={{ marginBottom: 16 }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Platform</label>
            <select
              className="input"
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              style={{ width: 200 }}
            >
              {PLATFORMS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <div className="spacer" />
          {updatedAt && (
            <span className="muted" style={{ fontSize: 13 }}>
              Last updated: {new Date(updatedAt).toLocaleString()}
            </span>
          )}
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {loading ? (
          <div className="spinner" />
        ) : (
          <>
            <div className="field">
              <label>Configuration (JSON)</label>
              <textarea
                className="input"
                style={{ minHeight: 320 }}
                value={text}
                onChange={(e) => setText(e.target.value)}
                readOnly={!isAdmin}
                spellCheck={false}
              />
            </div>
            {isAdmin ? (
              <div className="row">
                <button className="btn btn-primary" onClick={save} disabled={saving}>
                  {saving ? "Saving…" : "Save defaults"}
                </button>
                <button className="btn btn-outline" onClick={() => load(platform)} disabled={saving}>
                  Reset
                </button>
              </div>
            ) : (
              <p className="muted" style={{ fontSize: 13.5 }}>
                Editing defaults requires the admin role.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
